import {
  StoreCatalog,
  SaveOfferView,
  SaveCategoryView,
  SavePrinterResponse,
  SaveStationResponse,
  ItemTag,
} from '../stores-catalog';
import {
  StoresCatalogAction,
  StoresCatalogActionType,
} from './stores-catalog.actions';
import { combineReducers } from '@ngrx/store';
import { Availability } from '../../stores';
import { StoresAction, StoresActionType } from '../../+state/stores.actions';

export enum CatalogImportStatus {
  PROCESSING = 'PROCESSING',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  PROCESSING_SUCCESS = 'PROCESSING_SUCCESS',
  IMPORTING = 'IMPORTING',
  IMPORTING_FAILED = 'IMPORTING_FAILED',
  IMPORTING_SUCCESS = 'IMPORTING_SUCCESS',
}

export enum CatalogImportType {
  CATALOG_PDF_IMAGE = 'CATALOG_PDF_IMAGE',
  CATALOG_EXCEL = 'CATALOG_EXCEL',
  CATALOG_OPTIONS_EXCEL = 'CATALOG_OPTIONS_EXCEL',
  PRODUCT_EXCEL = 'PRODUCT_EXCEL',
  PRODUCT_UPDATE_EXCEL = 'PRODUCT_UPDATE_EXCEL',
  CATALOG_TRANSLATIONS_EXCEL = 'CATALOG_TRANSLATIONS_EXCEL',
}

export interface CatalogImport {
  id: number;
  startAt: Date;
  finishAt: Date;
  fileName: string;
  status: CatalogImportStatus;
  storeId: number;
  catalogId: number;
  catalogData: StoreCatalog;
  type: CatalogImportType;
  totalProducts?: number;
  validProducts?: number;
  invalidProducts?: number;
  errorMessages?: string[];
  errors: string;
}

export interface CatalogList {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  updateStatus: 'INITIAL' | 'UPDATING' | 'SUCCESS' | 'FAILED';
  data: StoreCatalog;
  errorMessage: any;
  selectedCatalogIds: number[];
  catalogImport: CatalogImport;
}
export interface CatalogState {
  catalog: Catalog;
}

export interface Catalog {
  cataloglist: CatalogList;
  catalogDeleteError: boolean;
  itemTags: ItemTagList;
}

/**
 * The catalog's item tags (#11750). `saveStatus` and `savedTag` let the item
 * page's Add Tag dialog close on success; `duplicateLabel` is the field error
 * of a label the catalog already has.
 */
export interface ItemTagList {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  data: ItemTag[];
  saveStatus: 'INITIAL' | 'SAVING' | 'SAVED' | 'FAILED';
  savedTag: ItemTag | null;
  duplicateLabel: boolean;
}

export const catalogInitialState: Catalog = {
  cataloglist: {
    status: 'INITIAL',
    updateStatus: 'INITIAL',
    data: null,
    errorMessage: '',
    selectedCatalogIds: [],
    catalogImport: null,
  },
  catalogDeleteError: false,
  itemTags: {
    status: 'INITIAL',
    data: [],
    saveStatus: 'INITIAL',
    savedTag: null,
    duplicateLabel: false,
  },
};

export function cataloglist(
  state: CatalogList = catalogInitialState.cataloglist,
  action: StoresCatalogAction | StoresAction,
): CatalogList {
  switch (action.type) {
    case StoresActionType.LoadStore:
      return { ...catalogInitialState[''], status: 'LOADING' };
    case StoresActionType.LoadStoreFailed:
      return { ...catalogInitialState[''] };
    case StoresCatalogActionType.LoadCatalog:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.LoadCatalogSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: { ...state.data, ...action.catalog },
        errorMessage: '',
        selectedCatalogIds: state.selectedCatalogIds
          ? state.selectedCatalogIds
          : [],
      };
    case StoresCatalogActionType.LoadStation:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.LoadStationSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: { ...state.data, stations: [...action.stations] },
      };
    case StoresCatalogActionType.LoadPrinter:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.LoadPrinterSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: { ...state.data, printers: [...action.printers] },
      };
    case StoresCatalogActionType.SavePrinterSuccess: {
      const printers = (state.data?.printers || []) as SavePrinterResponse[];
      const saved = action.savePrinterResponse;
      const existingIndex = printers.findIndex(
        (p) => p.printerId === saved.printerId,
      );
      const updatedPrinters = [...printers];
      if (existingIndex >= 0) {
        updatedPrinters[existingIndex] = {
          ...updatedPrinters[existingIndex],
          ...saved,
        };
      } else {
        updatedPrinters.push(saved);
      }
      return {
        ...state,
        data: { ...state.data, printers: updatedPrinters },
      };
    }
    case StoresCatalogActionType.UpdatePrinterSuccess: {
      const printers = (state.data?.printers || []) as SavePrinterResponse[];
      const saved = action.savePrinterResponse;
      const updatedPrinters = printers.map((p) =>
        p.printerId === saved.printerId ? { ...p, ...saved } : p,
      );
      return {
        ...state,
        data: { ...state.data, printers: updatedPrinters },
      };
    }
    case StoresCatalogActionType.DeletePrinterSuccess: {
      const printers = (state.data?.printers || []) as SavePrinterResponse[];
      const updatedPrinters = printers.filter(
        (p) => p.printerId !== action.printerId,
      );
      return {
        ...state,
        data: { ...state.data, printers: updatedPrinters },
      };
    }
    case StoresCatalogActionType.VerifyCatalogDataSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: { ...state.data, ...action.catalog },
        errorMessage: '',
      };
    case StoresCatalogActionType.VerifyCatalogImageSuccess:
      return {
        ...state,
        status: 'LOADED',
        catalogImport: { ...state.catalogImport, ...action.catalogImport },
        errorMessage: '',
      };
    case StoresCatalogActionType.VerifyProductFileSuccess:
      return {
        ...state,
        status: 'LOADED',
        catalogImport: { ...state.catalogImport, ...action.catalogImport },
        errorMessage: '',
      };
    case StoresCatalogActionType.UploadCatalogDataSuccess:
    case StoresCatalogActionType.UploadCatalogTranslateSuccess:
      return {
        ...state,
        status: 'LOADED',
        catalogImport: { ...action.catalogImport },
        errorMessage: '',
      };
    case StoresCatalogActionType.LoadCatalogFailed:
      return { ...catalogInitialState[''], status: 'FAILED' };
    case StoresCatalogActionType.VerifyCatalogDataFailed:
      return { ...catalogInitialState[''], status: 'FAILED' };
    case StoresCatalogActionType.VerifyProductFileFailed:
      return { ...state, status: 'FAILED', catalogImport: null };
    case StoresCatalogActionType.BulkOfferChangeStatus:
      return { ...state, updateStatus: 'UPDATING' };
    case StoresCatalogActionType.BulkOfferChangeStatusSuccess:
      return {
        ...state,
        selectedCatalogIds: action.selectedCatalogIds,
        updateStatus: 'SUCCESS',
      };
    case StoresCatalogActionType.DeleteMultipleOfferSuccess:
      return {
        ...state,
        selectedCatalogIds: action.selectedCatalogIds,
        updateStatus: 'FAILED',
      };
    case StoresCatalogActionType.LoadPendingCatalogImportSuccess:
      return {
        ...state,
        catalogImport: { ...action.catalogImport },
        data: { ...action.catalogImport?.catalogData },
      };
    case StoresCatalogActionType.LoadPendingCatalogImportFailed:
      return { ...state, data: null, catalogImport: null };
    case StoresCatalogActionType.LoadPendingProductImportSuccess:
      return {
        ...state,
        catalogImport: { ...action.catalogImport },
        data: { ...action.catalogImport?.catalogData },
      };
    case StoresCatalogActionType.LoadPendingProductImportFailed:
      return { ...state, data: null, catalogImport: null };
    case StoresCatalogActionType.RemovePendingProductImportSuccess:
      return {
        ...state,
        data: { ...catalogInitialState.cataloglist.data },
        catalogImport: { ...catalogInitialState.cataloglist.catalogImport },
      };
    case StoresCatalogActionType.RemovePendingCatalogImportSuccess:
      return {
        ...state,
        data: { ...catalogInitialState.cataloglist.data },
        catalogImport: { ...catalogInitialState.cataloglist.catalogImport },
      };
    case StoresCatalogActionType.PatchSaveOffer:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.PatchSaveOfferSuccess:
      return {
        ...state,
        status: 'LOADED',
        data: {
          ...state.data,
          categories: state.data?.categories?.map((category) => ({
            ...category,
            offers: category.offers?.map((offer) =>
              offer.offerId === action.saveOffer.offerId
                ? { ...offer, ...action.saveOffer }
                : offer,
            ),
          })),
        },
      };
    case StoresCatalogActionType.PatchSaveOfferFailed:
      return { ...catalogInitialState[''], status: 'FAILED' };
    default:
      return state;
  }
}

export function catalogDeleteError(
  state: boolean = false,
  action: StoresCatalogAction | StoresAction,
): boolean {
  switch (action.type) {
    case StoresCatalogActionType.DeleteCatalogFailed:
      return action.catalogDeleteError;
    case StoresCatalogActionType.DeleteCatalogSuccess:
      return false;
    case StoresCatalogActionType.ClearDeleteCatalogError:
      return false;
    default:
      return state;
  }
}

export const ITEM_TAG_ALREADY_EXIST = 'ITEM_TAG_ALREADY_EXIST';

export function itemTags(
  state: ItemTagList = catalogInitialState.itemTags,
  action: StoresCatalogAction | StoresAction,
): ItemTagList {
  switch (action.type) {
    case StoresCatalogActionType.LoadItemTags:
      // Drop the previous catalog's tags so none is matched while loading.
      return { ...state, status: 'LOADING', data: [] };
    case StoresCatalogActionType.LoadItemTagsSuccess:
    case StoresCatalogActionType.SaveItemTagPositionsSuccess:
      return { ...state, status: 'LOADED', data: [...action.tags] };
    case StoresCatalogActionType.LoadItemTagsFailed:
      return { ...state, status: 'FAILED', data: [] };
    case StoresCatalogActionType.SaveItemTag:
    case StoresCatalogActionType.UpdateItemTag:
      return {
        ...state,
        saveStatus: 'SAVING',
        savedTag: null,
        duplicateLabel: false,
      };
    case StoresCatalogActionType.SaveItemTagSuccess:
      return {
        ...state,
        data: [...state.data, action.tag],
        saveStatus: 'SAVED',
        savedTag: action.tag,
      };
    case StoresCatalogActionType.UpdateItemTagSuccess:
      return {
        ...state,
        data: state.data.map((tag) =>
          tag.id === action.tag.id ? action.tag : tag,
        ),
        saveStatus: 'SAVED',
        savedTag: action.tag,
      };
    case StoresCatalogActionType.SaveItemTagFailed:
    case StoresCatalogActionType.UpdateItemTagFailed:
      return {
        ...state,
        saveStatus: 'FAILED',
        duplicateLabel: action.errorMessage?.code === ITEM_TAG_ALREADY_EXIST,
      };
    case StoresCatalogActionType.DeleteItemTagSuccess:
      return {
        ...state,
        data: state.data.filter((tag) => tag.id !== action.tagId),
      };
    case StoresCatalogActionType.ClearItemTagSaveState:
      return {
        ...state,
        saveStatus: 'INITIAL',
        savedTag: null,
        duplicateLabel: false,
      };
    default:
      return state;
  }
}

const reducerCatalog: (state: Catalog, action: StoresCatalogAction) => Catalog =
  combineReducers({
    cataloglist,
    catalogDeleteError,
    itemTags,
  });

export function storesCatalogReducer(
  state: Catalog = catalogInitialState,
  action: StoresCatalogAction,
): Catalog {
  return reducerCatalog(state, action);
}
/**
 * Offer related reducer start
 */
export interface OfferState {
  offer: Offer;
}

export interface Offer {
  offerDetails: SaveOfferList;
  imageUrl: string;
  availability: Availability;
}
export interface SaveOfferList {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  data: SaveOfferView;
  errorMessage: any;
}
export const OfferInitialState: Offer = {
  offerDetails: {
    status: 'INITIAL',
    data: null,
    errorMessage: '',
  },
  imageUrl: '',
  availability: null,
};

export function offerDetails(
  state: SaveOfferList = OfferInitialState.offerDetails,
  action: StoresCatalogAction,
): SaveOfferList {
  switch (action.type) {
    case StoresCatalogActionType.LoadOffer:
      return { status: 'LOADING', ...OfferInitialState[''] };
    case StoresCatalogActionType.LoadOfferSuccess:
      return {
        status: 'LOADED',
        data: action.offerView,
        errorMessage: '',
      };
    case StoresCatalogActionType.LoadOfferFailed:
      return { ...OfferInitialState[''], status: 'FAILED' };
    default:
      return state;
  }
}

export function imageUrl(
  state: string = OfferInitialState.imageUrl,
  action: StoresCatalogAction,
): string {
  switch (action.type) {
    case StoresCatalogActionType.UploadOfferImageSuccess:
      // A variant icon upload (#11754) is not the item image.
      return action.target === 'variantIcon' ? state : action.imageUrl;
    case StoresCatalogActionType.LoadOfferSuccess:
      return action.offerView.standardImage;
    case StoresCatalogActionType.UploadOfferOrCategoryImageFailed:
      // A failed variant icon upload (#11754) leaves the item image as it was.
      return action.target === 'variantIcon'
        ? state
        : { ...OfferInitialState[''], status: 'FAILED' };
    default:
      return state;
  }
}

export function availability(
  state: Availability = OfferInitialState.availability,
  action: StoresCatalogAction,
): Availability {
  switch (action.type) {
    case StoresCatalogActionType.SaveAvailabilitySuccess:
      return action.availability;
    case StoresCatalogActionType.SaveAvailabilityFailed:
      return { ...OfferInitialState[''], status: 'FAILED' };
    default:
      return state;
  }
}

const reducerOffer: (state: Offer, action: StoresCatalogAction) => Offer =
  combineReducers({
    offerDetails,
    imageUrl,
    availability,
  });

export function storeOfferReducer(
  state: Offer = OfferInitialState,
  action: StoresCatalogAction,
): Offer {
  return reducerOffer(state, action);
}
/**
 * Offer related reducer end
 */

/**
 * Category related reducer start
 */
export interface CategoryState {
  category: Category;
}

export interface Category {
  categoryDetails: SaveCategoryList;
  imageUrl: string;
}
export interface SaveCategoryList {
  status: 'INITIAL' | 'LOADING' | 'LOADED' | 'FAILED';
  data: SaveCategoryView;
  errorMessage: any;
  stationData: SaveStationResponse;
  printerData: SavePrinterResponse;
}

export const CategoryInitialState: Category = {
  categoryDetails: {
    status: 'INITIAL',
    data: null,
    errorMessage: '',
    stationData: null,
    printerData: null,
  },
  imageUrl: '',
};

export function categoryDetails(
  state: SaveCategoryList = CategoryInitialState.categoryDetails,
  action: StoresCatalogAction,
): SaveCategoryList {
  switch (action.type) {
    case StoresCatalogActionType.LoadCategory:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.LoadCategorySuccess:
      return {
        status: 'LOADED',
        data: action.categoryView,
        errorMessage: '',
        stationData: state.stationData,
        printerData: state.printerData,
      };
    case StoresCatalogActionType.LoadCategoryFailed:
      return { ...CategoryInitialState[''], status: 'FAILED' };
    case StoresCatalogActionType.LoadStationCategory:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.LoadStationCategorySuccess:
      return {
        ...state,
        status: 'LOADED',
        stationData: action.station,
        errorMessage: '',
      };
    case StoresCatalogActionType.LoadStationCategoryFailed:
      return { ...CategoryInitialState[''], status: 'FAILED' };
    case StoresCatalogActionType.LoadPrinterDetails:
      return { ...state, status: 'LOADING' };
    case StoresCatalogActionType.LoadPrinterDetailsSuccess:
      return {
        ...state,
        status: 'LOADED',
        printerData: action.printer,
        errorMessage: '',
      };
    case StoresCatalogActionType.LoadPrinterDetailsFailed:
      return { ...CategoryInitialState[''], status: 'FAILED' };
    default:
      return state;
  }
}

export function categoryImageUrl(
  state: string = OfferInitialState.imageUrl,
  action: StoresCatalogAction,
): string {
  switch (action.type) {
    case StoresCatalogActionType.UploadCategoryImageSuccess:
      return action.imageUrl;
    case StoresCatalogActionType.LoadCategorySuccess:
      return action.categoryView.standardImage;
    case StoresCatalogActionType.UploadOfferOrCategoryImageFailed:
      return { ...OfferInitialState[''], status: 'FAILED' };
    default:
      return state;
  }
}

const reducerCategory: (
  state: Category,
  action: StoresCatalogAction,
) => Category = combineReducers({
  categoryDetails,
  imageUrl: categoryImageUrl,
});

export function storeCategoryReducer(
  state: Category = CategoryInitialState,
  action: StoresCatalogAction,
): Category {
  return reducerCategory(state, action);
}

export interface SaveCategoryState {
  saveCategory: SaveCategory;
}

export interface SaveCategory {
  saveCategoryDetails: SaveCategoryDetails;
}

export interface SaveCategoryDetails {
  status: 'INITIAL' | 'SAVING' | 'SAVED' | 'FAILED';
}

export const SaveCategoryInitialState: SaveCategory = {
  saveCategoryDetails: { status: 'INITIAL' },
};

export function saveCategoryDetails(
  state: SaveCategoryDetails = SaveCategoryInitialState.saveCategoryDetails,
  action: StoresCatalogAction,
): SaveCategoryDetails {
  switch (action.type) {
    case StoresCatalogActionType.SaveCategory:
      return { ...state, status: 'SAVING' };
    case StoresCatalogActionType.SaveCategorySuccess:
      return { ...state, status: 'SAVED' };
    case StoresCatalogActionType.SaveCategoryFailed:
      return { ...state, status: 'FAILED' };
    default:
      return state;
  }
}

const reducerSaveCategory: (
  state: SaveCategory,
  action: StoresCatalogAction,
) => SaveCategory = combineReducers({
  saveCategoryDetails,
});

export function storeSaveCategoryReducer(
  state: SaveCategory = SaveCategoryInitialState,
  action: StoresCatalogAction,
): SaveCategory {
  return reducerSaveCategory(state, action);
}
